/**
 * Color palette for series (matches BoxPlotBasic)
 */
const COLOR_RANGE = [
  "#003e6b",
  "#A63EE5",
  "#6A984D",
  "#008BBF",
  "#AF7832",
  "#BE2A27",
  "#a63791",
];

/**
 * Generates a basic line plot specification for Vega-Lite based on the selected data.
 * Supports multi-series data with fixed linear interpolation.
 *
 * Data format:
 * - First column: x-axis values (numeric or nominal)
 * - Subsequent columns: y-values for each series
 * - First row (optional): headers if non-numeric
 *
 * @param {Array} selectedData - The data selected from the spreadsheet, where each row represents a data point.
 * @returns {Object} - Returns the Vega-Lite specification for the line plot along with plot metadata (unitX, unitY, headerX, headerY).
 */
export const LinePlotBasic = (selectedData) => {
  if (!selectedData || selectedData.length === 0) {
    return null;
  }

  if (!selectedData[0] || selectedData[0].length < 2) {
    return null;
  }

  const values = [];
  const seriesNames = [];
  let unitX;
  let unitY;
  let headerX;
  let headerY;
  let firstDataRow = 0;

  /**
   * Helper function to check if a value is numeric
   */
  const isNumeric = (val) => !isNaN(parseFloat(val));

  /**
   * Helper function to extract cell value
   */
  const getCellValue = (cell) => {
    if (!cell) return null;
    return Object.prototype.hasOwnProperty.call(cell, "m") ? cell.m : null;
  };

  /**
   * Helper function to extract cell format for units
   */
  const getCellFormat = (cell) => {
    if (!cell || !Object.prototype.hasOwnProperty.call(cell, "ct")) return "";
    return cell.ct.fa || "";
  };

  /**
   * Helper function to determine unit from format
   */
  const setFormat = (format) => {
    if (format.includes("%")) {
      return "%";
    }
    return null;
  };

  /**
   * Process first row to check for headers
   */
  if (selectedData.length > 0 && selectedData[0].length > 1) {
    const firstRowFirstCol = getCellValue(selectedData[0][0]);
    const firstRowSecondCol = getCellValue(selectedData[0][1]);

    if (
      firstRowFirstCol !== null &&
      firstRowSecondCol !== null &&
      !isNumeric(firstRowFirstCol) &&
      !isNumeric(firstRowSecondCol)
    ) {
      headerX = firstRowFirstCol;

      for (let col = 1; col < selectedData[0].length; col++) {
        const seriesName = getCellValue(selectedData[0][col]);
        seriesNames.push(seriesName || `Series ${col}`);
      }

      if (seriesNames.length === 1) {
        headerY = seriesNames[0];
      }

      firstDataRow = 1;
    } else {
      for (let col = 1; col < selectedData[0].length; col++) {
        seriesNames.push(`Series ${col}`);
      }
    }
  }

  /**
   * Extract units from first data row
   */
  if (selectedData.length > firstDataRow) {
    const xFormat = getCellFormat(selectedData[firstDataRow][0]);
    unitX = setFormat(xFormat);

    if (selectedData[firstDataRow].length > 1) {
      const yFormat = getCellFormat(selectedData[firstDataRow][1]);
      unitY = setFormat(yFormat);
    }
  }

  /**
   * Process data rows and transform into plot values
   */
  for (
    let rowIndex = firstDataRow;
    rowIndex < selectedData.length;
    rowIndex++
  ) {
    const row = selectedData[rowIndex];

    if (!row || !row[0]) continue;

    const xValue = getCellValue(row[0]);
    if (xValue === null) continue;

    const xParsed = isNumeric(xValue) ? parseFloat(xValue) : xValue;

    for (let col = 1; col < row.length; col++) {
      const yValue = getCellValue(row[col]);

      if (yValue !== null && isNumeric(yValue)) {
        values.push({
          x: xParsed,
          y: parseFloat(yValue),
          series: seriesNames[col - 1] || `Series ${col}`,
        });
      }
    }
  }

  /**
   * Return null if no valid data was extracted
   */
  if (values.length === 0) {
    return null;
  }

  /**
   * Determine x-axis type based on first value
   */
  const xAxisType = isNumeric(values[0].x) ? "quantitative" : "nominal";

  /**
   * Count unique series to determine if we need a legend
   */
  const uniqueSeries = [...new Set(values.map((v) => v.series))];
  const isMultiSeries = uniqueSeries.length > 1;

  /**
   * Build and return spec - completely separate paths for single vs multi
   */
  if (isMultiSeries) {
    return {
      spec: {
        $schema: "https://vega.github.io/schema/vega-lite/v5.json",
        padding: 5,
        autosize: {
          type: "fit",
          resize: true,
          contains: "padding",
        },
        data: {
          values: values,
        },
        mark: {
          type: "line",
          point: true,
          strokeWidth: 2,
          interpolate: "linear",
          tooltip: true,
        },
        encoding: {
          x: {
            field: "x",
            type: xAxisType,
            axis: {
              title: null,
              grid: false,
            },
          },
          y: {
            field: "y",
            type: "quantitative",
            axis: {
              title: null,
              grid: true,
              labelExpr: `datum.value + '${unitY ? unitY : ""}'`,
            },
          },
          color: {
            field: "series",
            type: "nominal",
            legend: {
              title: "Series",
              orient: "right",
            },
            scale: {
              range: COLOR_RANGE,
            },
          },
          tooltip: [
            { field: "x", type: xAxisType, title: headerX || "x" },
            { field: "y", type: "quantitative", title: headerY || "y" },
            { field: "series", type: "nominal", title: "Series" },
          ],
        },
      },
      unitX,
      unitY,
      headerX,
      headerY,
    };
  } else {
    return {
      spec: {
        $schema: "https://vega.github.io/schema/vega-lite/v5.json",
        padding: 5,
        autosize: {
          type: "fit",
          resize: true,
          contains: "padding",
        },
        data: {
          values: values,
        },
        mark: {
          type: "line",
          point: true,
          strokeWidth: 2,
          interpolate: "linear",
          tooltip: true,
        },
        encoding: {
          x: {
            field: "x",
            type: xAxisType,
            axis: {
              title: null,
              grid: false,
            },
          },
          y: {
            field: "y",
            type: "quantitative",
            axis: {
              title: null,
              grid: true,
              labelExpr: `datum.value + '${unitY ? unitY : ""}'`,
            },
          },
          color: {
            field: "series",
            type: "nominal",
            legend: null,
            scale: {
              range: COLOR_RANGE,
            },
          },
          tooltip: [
            { field: "x", type: xAxisType, title: headerX || "x" },
            { field: "y", type: "quantitative", title: headerY || "y" },
            { field: "series", type: "nominal", title: "Series" },
          ],
        },
      },
      unitX,
      unitY,
      headerX,
      headerY,
    };
  }
};
